import * as Sentry from "@sentry/react"

export interface FilterValue {
  value: any
  fieldUUID: string
  operation: string
  rangeValue?: any
  /**
   * Filtre portant sur PLUSIEURS champs, en OU — la barre de recherche multi-champs.
   *
   * `fieldUUID`/`value` ne savent désigner qu'une colonne : chercher « encaissement » à la fois
   * dans le code et dans le libellé d'un contrôle ne s'exprime pas avec eux. `getTransformedFilterFromPage`
   * sait déjà lire cette forme et en faire un bloc `or` ; elle n'était simplement jamais produite.
   *
   * ⚠️ `fieldUUID` et `value` restent renseignés quand `conditions` l'est : ils portent le premier
   * champ, et c'est ce que lisent les chemins qui ne connaissent pas encore le multi-champs (dont
   * l'affichage de la valeur choisie).
   */
  conditions?: Array<{ fieldUUID: string; operation: string; value: any }>
}

type Filters = Record<string, FilterValue>

/** Les filtres de la page actuellement affichée. */
const store = new Map<string, FilterValue>()

/**
 * Ce que l'utilisateur avait choisi sur chaque page déjà visitée, pour le lui rendre s'il y revient.
 *
 * Pourquoi ça existe : ouvrir une fiche depuis un tableau puis revenir démonte la page, et les
 * filtres partaient avec elle — il fallait tout resaisir. C'est le reproche que le client a fait
 * deux fois, sur Contrôle Interne et déjà sur Audit.
 *
 * ⚠️ L'instantané est réécrit à chaque `set`/`remove`, **pas** au départ de la page. Deux raisons :
 * il ne peut jamais être en retard sur ce qui est affiché, et surtout un instantané n'existe que si
 * l'utilisateur a réellement agi. Sauvegarder au démontage produirait un instantané vide dès le
 * premier montage — React monte, démonte et remonte les composants en développement — et la page
 * croirait avoir déjà été visitée alors qu'on vient de l'ouvrir.
 *
 * Durée de vie : l'onglet. Rien n'est écrit sur le disque, donc un rechargement repart des valeurs
 * par défaut. C'est délibéré : un filtre survivant à un rechargement désignerait un jour un widget
 * ou une colonne disparus, et l'écran s'ouvrirait en erreur sans que personne comprenne pourquoi.
 */
const snapshots = new Map<string, Filters>()

/** La page dont `store` porte les filtres, ou `null` hors d'une page. */
let currentPageId: string | null = null

/**
 * Un instantané a-t-il été restauré sur la page courante ?
 *
 * Lu par les widgets, pas seulement par la page : un filtre à valeur par défaut DYNAMIQUE ne peut
 * pas se poser à l'ouverture — il attend que ses options arrivent — donc il passe APRÈS la
 * restauration et doit savoir s'y soumettre. Sans ça, il réécrirait un filtre que l'utilisateur
 * venait délibérément de vider, ce que la règle des valeurs par défaut interdit partout ailleurs.
 */
let restaure = false

function toPlain(): Filters {
  const out: Filters = {}
  store.forEach((v, k) => { out[k] = v })
  return out
}

/** Aligne l'instantané de la page courante sur ce qui est affiché. */
function memoriser() {
  if (currentPageId) snapshots.set(currentPageId, toPlain())
}

function dispatch(eventName: string, detail?: any) {
  window.dispatchEvent(new CustomEvent(eventName, { detail }))
}

function notifierChangement(widgetId: string | null = null, filter: FilterValue | null = null) {
  dispatch("page:filter-changed", { widgetId, filter, allFilters: toPlain() })
}

export const filterStore = {
  get(widgetId: string): FilterValue | undefined {
    return store.get(widgetId)
  },

  getAll(): Filters {
    return toPlain()
  },

  set(widgetId: string, filter: FilterValue) {
    store.set(widgetId, filter)
    memoriser()

    // 🟢 Sentry Breadcrumb pour le debug du dashboard
    Sentry.addBreadcrumb({
      category: "filter",
      message: `Filter set for widget ${widgetId}`,
      data: { fieldUUID: filter.fieldUUID, operation: filter.operation },
      level: "info",
    })

    notifierChangement(widgetId, filter)
  },

  remove(widgetId: string) {
    store.delete(widgetId)
    memoriser()

    Sentry.addBreadcrumb({
      category: "filter",
      message: `Filter removed for widget ${widgetId}`,
      level: "info",
    })

    notifierChangement(widgetId, null)
  },

  /**
   * Entre sur une page : range les filtres de la précédente et repart d'un affichage vide.
   *
   * ⚠️ N'ouvre **pas** l'instantané de la page — c'est le rôle de `restoreCurrentPage`. Les deux
   * sont séparés parce qu'ils ne peuvent pas avoir lieu au même moment : on sait sur quelle page on
   * arrive dès l'URL, mais on ne sait qu'à l'arrivée des données si cette page a demandé à
   * conserver ses filtres.
   */
  enterPage(pageId: string | null | undefined) {
    const cible = pageId || null
    if (cible === currentPageId) return

    memoriser()
    store.clear()
    currentPageId = cible
    restaure = false

    Sentry.addBreadcrumb({
      category: "filter",
      message: `Entered page ${cible}`,
      level: "info",
    })

    notifierChangement()
  },

  /**
   * Ressort les filtres que l'utilisateur avait laissés sur la page courante.
   *
   * À n'appeler que si la page a activé la conservation des filtres : le réglage est par défaut
   * inactif, pour que les dashboards existants ne changent pas de comportement.
   *
   * @returns `true` si un instantané a été restauré — c'est-à-dire si l'utilisateur était déjà venu
   * sur cette page et y avait touché aux filtres. L'appelant s'en sert pour **ne pas** réappliquer
   * par-dessus les valeurs par défaut des widgets : un choix explicite prime sur un défaut, y
   * compris le choix d'avoir vidé un filtre.
   */
  restoreCurrentPage(): boolean {
    const memorise = currentPageId ? snapshots.get(currentPageId) : undefined
    if (!memorise) return false

    store.clear()
    Object.entries(memorise).forEach(([widgetId, filter]) => store.set(widgetId, filter))
    restaure = true

    Sentry.addBreadcrumb({
      category: "filter",
      message: `Filters restored for page ${currentPageId}`,
      level: "info",
    })

    notifierChangement()
    return true
  },

  /**
   * Quitte la page courante. L'instantané est déjà à jour (il l'est à chaque `set`/`remove`), on ne
   * fait donc que vider ce qui est affiché — sans quoi les filtres de la page qu'on quitte
   * s'appliqueraient une fraction de seconde à celle qui arrive.
   */
  /** La page courante a-t-elle retrouvé des filtres mémorisés ? */
  wasRestored(): boolean {
    return restaure
  },

  leavePage() {
    store.clear()
    currentPageId = null
    restaure = false
    dispatch("page:filter-cleared", {})
  },

  /**
   * Écarte les filtres restaurés qui désignent un widget absent de la page.
   *
   * Sans cela, un filtre survivant à la suppression de son widget continuerait de viser une colonne
   * qui a pu disparaître du cube : la requête échouerait en « colonne introuvable » et la page
   * entière s'ouvrirait vide. On ne peut le vérifier qu'ici, une fois les widgets chargés.
   *
   * ⚠️ `pageId` doit être celui que portent les WIDGETS (leur `dashboardId`), pas celui de l'URL.
   * Une réponse tardive pour la page qu'on vient de quitter arrive après la navigation : comparée à
   * l'URL elle passerait pour légitime, et effacerait les filtres de la page où l'on se trouve
   * désormais. Comparée à la page dont elle parle vraiment, elle est ignorée.
   *
   * Un `pageId` ABSENT — ou d'un type inattendu — nettoie quand même, délibérément : rien ne
   * garantit que la charge utile des widgets porte ce champ, ni sous quelle forme (les identifiants
   * y voyagent parfois en `{ $oid }`). Ne sauter QUE sur une chaîne qui diffère vraiment ; refuser
   * dès que la comparaison est douteuse transformerait une incertitude de format en garde-fou mort,
   * alors que nettoyer sans pouvoir vérifier la provenance reste le comportement d'avant.
   */
  pruneToWidgets(pageId: unknown, widgetIds: string[]) {
    if (typeof pageId === "string" && pageId !== "" && pageId !== currentPageId) return

    const vivants = new Set(widgetIds)
    const orphelins = Array.from(store.keys()).filter(id => !vivants.has(id))
    if (orphelins.length === 0) return

    orphelins.forEach(id => store.delete(id))
    memoriser()

    Sentry.addBreadcrumb({
      category: "filter",
      message: `Filters dropped for ${orphelins.length} missing widget(s)`,
      level: "info",
    })

    notifierChangement()
  },

  /**
   * Remise à zéro totale : les filtres affichés **et** la mémoire de toutes les pages. À réserver
   * aux cas où l'on veut vraiment tout oublier ; pour un simple changement de page, `enterPage`.
   */
  clear() {
    store.clear()
    snapshots.clear()
    currentPageId = null
    restaure = false

    Sentry.addBreadcrumb({
      category: "filter",
      message: "All filters cleared",
      level: "info",
    })

    dispatch("page:filter-cleared", {})
  },
}
